import { useState } from 'react';

import { api, ApiError } from '../../api';
import { Session } from '../../auth';
import { GREEK_MOBILES_ONLY } from '../../joining';
import { Panel, Segmented } from '../../ui';

// The internal ISO-operator console (DECISION-059) — the operator/billing console
// the roadmap spine flagged, here as its first surface: a form that mints a hotel
// or supplier tenant + its first sign-in identity through POST /v1/accounts (the
// one provisioning primitive). Its own minimal shell — no hotel/supplier rail —
// because an operator is a third principal that reaches no tenant screen. NOT one
// of the 41 registered marketplace screens (a register decision, recorded).
//
// A supplier minted here is set up by someone else (DECISION-080 §9): it opens
// on "here's what was set up for you", price first, to keep or change before it
// can open for work — so the operator may set up a first trip and its price, and
// is told what to say to the supplier (B35), not "give them this id". The phone
// must be a Greek mobile: only those get sign-in codes for now (§8).

type AccType = 'hotel' | 'supplier';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function AdminConsole({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const [type, setType] = useState<AccType>('hotel');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('Europe/Athens');
  // An optional first trip and its price, for the supplier to keep or change.
  const [tripName, setTripName] = useState('');
  const [tripKind, setTripKind] = useState<'experience' | 'transfer'>('experience');
  const [tripPrice, setTripPrice] = useState('');
  const [tripPer, setTripPer] = useState<'guest' | 'job'>('guest');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{
    accountId: string;
    accountType: string;
    created: boolean;
    identity: string;
    trip: boolean;
  } | null>(null);

  const identityOk = type === 'hotel' ? EMAIL_RE.test(email.trim()) : /^\+?\d{8,15}$/.test(phone.replace(/[\s().-]/g, ''));
  // The price as typed ("58" or "58,50"), read straight into integer minor units
  // — never through a float (hard constraint 7). The API checks it again.
  const priceMatch = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(tripPrice.trim());
  const priceMinor = priceMatch ? Number(priceMatch[1]) * 100 + Number((priceMatch[2] ?? '').padEnd(2, '0')) : null;
  const tripStarted = type === 'supplier' && (tripName.trim() !== '' || tripPrice.trim() !== '');
  const tripOk = !tripStarted || (!!tripName.trim() && priceMinor !== null);
  const canSubmit = !!name.trim() && identityOk && tripOk && !busy;

  const submit = async () => {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const body: Record<string, unknown> = { accountType: type, name: name.trim(), timezone: timezone.trim() };
      if (type === 'hotel') body.email = email.trim();
      else body.phone = phone.trim();
      if (tripStarted && priceMinor !== null) {
        body.firstProduct = {
          name: tripName.trim(),
          kind: tripKind,
          per: tripPer,
          supplierPrice: { minor: priceMinor, currency: 'EUR' },
        };
      }
      const r = await api<{ accountId: string; accountType: string; created: boolean }>('POST', '/accounts', body);
      setResult({ ...r, identity: type === 'hotel' ? email.trim() : phone.trim(), trip: tripStarted });
      setName('');
      setEmail('');
      setPhone('');
      setTripName('');
      setTripPrice('');
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const host = window.location.host;

  return (
    <div className="shell">
      <nav className="topnav">
        <span className="brandmark">I</span>
        <span className="crumb">
          <span className="here">ISO operator · new account</span>
        </span>
        <span className="right">
          <span className="property-pill">Operator</span>
          <span className="avatar">{session.name.slice(0, 1).toUpperCase()}</span>
          <button className="signout" onClick={onSignOut}>
            Sign out
          </button>
        </span>
      </nav>

      <main className="content" style={{ maxWidth: 560, margin: '0 auto', padding: 'var(--space-lg)' }}>
        <h1>Create an account</h1>
        <p className="welcome">
          Mint a hotel or supplier tenant and its first sign-in identity — the same primitive the
          invitation flow uses, and the one self-signup will call.
        </p>

        <div className="field">
          <label>Account type</label>
          <Segmented
            value={type}
            onChange={setType}
            options={[
              { value: 'hotel', label: 'Hotel' },
              { value: 'supplier', label: 'Supplier' },
            ]}
          />
        </div>

        <div className="field">
          <label htmlFor="acc-name">Business name</label>
          <input id="acc-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </div>

        {type === 'hotel' ? (
          <div className="field">
            <label htmlFor="acc-email">Sign-in email</label>
            <input id="acc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <p className="hint" style={{ marginTop: 6 }}>
              The hotel signs in with a magic link to this address — no password to hand over.
            </p>
          </div>
        ) : (
          <>
            <div className="field">
              <label htmlFor="acc-phone">Sign-in phone</label>
              <input id="acc-phone" className="mono" placeholder="+30 691 234 5678" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
                The supplier signs in with a 6-digit SMS code — the phone is their identifier, no password.{' '}
                {GREEK_MOBILES_ONLY}
              </p>
            </div>

            {/* DECISION-080 §9: what is set up here, the supplier sees first —
                price first — and keeps or changes before any hotel can book them. */}
            <div className="field">
              <label htmlFor="acc-trip">First trip (optional)</label>
              <input id="acc-trip" placeholder="Island Tour" value={tripName} onChange={(e) => setTripName(e.target.value)} maxLength={120} />
            </div>
            <div className="field">
              <label>What it is</label>
              <Segmented
                value={tripKind}
                onChange={setTripKind}
                options={[
                  { value: 'experience', label: 'An experience' },
                  { value: 'transfer', label: 'A transfer' },
                ]}
              />
            </div>
            <div className="field">
              <label htmlFor="acc-price">What they are paid, in euro</label>
              <input id="acc-price" className="mono" inputMode="decimal" placeholder="58" value={tripPrice} onChange={(e) => setTripPrice(e.target.value)} />
            </div>
            <div className="field">
              <label>Paid</label>
              <Segmented
                value={tripPer}
                onChange={setTripPer}
                options={[
                  { value: 'guest', label: 'Per guest' },
                  { value: 'job', label: 'For the whole trip' },
                ]}
              />
              <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
                The supplier sees this first, price first, and keeps it or changes it before any hotel can book them.
              </p>
            </div>
            {tripStarted && !tripOk && (
              <p className="hint" role="status">A first trip needs a name and a price like 58 or 58,50.</p>
            )}
          </>
        )}

        <div className="field">
          <label htmlFor="acc-tz">Timezone</label>
          <input id="acc-tz" className="mono" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
        </div>

        {error && (
          <p className="error-line" role="alert">
            {error}
          </p>
        )}

        <button className="btn lg" disabled={!canSubmit} onClick={submit} style={{ marginTop: 8 }}>
          {busy ? 'Creating…' : 'Create account'}
        </button>

        {result && (
          <div style={{ marginTop: 'var(--space-lg)' }}>
            <Panel>
              <strong>{result.created ? 'Account created.' : 'That identity already had an account.'}</strong>
              <div className="mono" style={{ marginTop: 6 }}>
                accountId {result.accountId}
              </div>
              {/* B35: what to SAY to them, not "give them this id". A supplier
                  signs in by phone alone; a hotel needs its account id. */}
              <div className="hint" style={{ marginTop: 4 }}>
                {result.accountType === 'hotel'
                  ? `Tell them: open ${host}/platform, enter account id ${result.accountId}, and choose "Email me a sign-in link instead" — the link goes to ${result.identity}.`
                  : `Tell them: open ${host}/app on their phone and sign in with ${result.identity} — a code comes by text. ${
                      result.trip ? 'They will see the trip and price you set up first, to keep or change' : 'They will see what was set up for them first, to keep or change'
                    }, and no hotel can book them until they have checked it and finished setting up.`}
              </div>
            </Panel>
          </div>
        )}
      </main>
    </div>
  );
}
